import { DocumentTemplateType, QueryFactory } from '@lmt/shared-types';
import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TemplateDto } from '@lmt/shared-types';
import { isDocumentNew } from './richedit-helper';
import { activateTemplateButtonId } from './ActivateTemplateRichEditButton';
import { isTemplateHeaderOrFooter } from './UpdateHeaderAndFooter';
import { useTranslation } from 'react-i18next';
import {
  CustomCommandExecutedEventArgs,
  DXR,
  EventArgs,
  RichEdit,
} from '../../../common/devexpress/RichEdit/dx-richedit';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useEventCallback } from '@mui/material';

export const deleteTemplateMenuId = 'deleteTemplateMenuId';

type Props = {
  richEdit: React.MutableRefObject<RichEdit>;
  currentTemplate: TemplateDto | null;
  templateType: DocumentTemplateType;
};
/*
  Adds "Delete Template" menu item to RichEdit and handles it
 */
export const DeleteTemplateRichEditButton: React.FC<Props> = (props) => {
  const { richEdit, currentTemplate } = props;
  const modals = useModal();
  const queryClient = useQueryClient();
  const i18n = useTranslation();
  const deleteTemplateCustomCommandExecutedEventHandler = useEventCallback(
    async (
      sender: RichEdit | undefined,
      args: CustomCommandExecutedEventArgs | undefined,
    ) => {
      if (!sender || !args) {
        return;
      }
      if (args.commandName !== deleteTemplateMenuId) return;

      if (
        isDocumentNew(sender) ||
        !currentTemplate?.id ||
        (currentTemplate.isDefault && isTemplateHeaderOrFooter(currentTemplate))
      ) {
        return;
      }

      if (
        !(await modals.showDeletionConfirmation({
          text: i18n.t('documents.delete_template_prompt.text', {
            template: sender.fileName,
          }),
        }))
      ) {
        return;
      }

      try {
        sender.loadingPanel.show();
        if (currentTemplate.type === DocumentTemplateType.HL7OutgoingMessage) {
          await QueryFactory.HL7OutgoingTemplatesClient.remove(
            currentTemplate.id,
          );
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.HL7OutgoingTemplatesQuery.getQueryKey(),
          });
        } else if (
          currentTemplate.type === DocumentTemplateType.GDTOutgoingMessage
        ) {
          await QueryFactory.GDTOutgoingTemplatesClient.remove(
            currentTemplate.id,
          );
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.GDTOutgoingTemplatesQuery.getQueryKey(),
          });
        } else {
          await QueryFactory.DocumentTemplateClient.remove(currentTemplate.id);
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.DocumentTemplateQuery.getQueryKey(),
          });
        }
        sender.newDocument();
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
        return false;
      } finally {
        sender.loadingPanel.hide();
      }
    },
  );

  useEffect(function AddDeleteTemplateHandler() {
    const richEditCurrent = richEdit.current;

    richEditCurrent.events.customCommandExecuted.addHandler(
      deleteTemplateCustomCommandExecutedEventHandler,
    );
    return () => {
      richEditCurrent.events.customCommandExecuted.removeHandler(
        deleteTemplateCustomCommandExecutedEventHandler,
      );
    };
  }, []);
  const removeDeleteButton = useEventCallback(() => {
    richEdit?.current?.updateRibbon((ribbon) => {
      if (!ribbon) return;
      const newFilePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
      newFilePanel.removeItem(deleteTemplateMenuId);
    });
  });

  const addDeleteTemplateMenuItem = useEventCallback(
    (sender: RichEdit | undefined, _e: EventArgs | undefined) => {
      if (!sender) {
        return;
      }

      const lastRibbonSettings = (sender as any)._native.lastRibbonSettings;
      const filePanel = lastRibbonSettings.getTab(DXR.RibbonTabType.File)!;
      const deleteMenuItem = filePanel.getItem(deleteTemplateMenuId);
      const shouldBeShown =
        !isDocumentNew(sender) &&
        currentTemplate &&
        !currentTemplate?.isDefault;

      if (!deleteMenuItem && shouldBeShown) {
        sender.updateRibbon((ribbon) => {
          if (!ribbon) return;
          const newFilePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
          newFilePanel.insertItemAfter(
            new DXR.RibbonButtonItem(
              deleteTemplateMenuId,
              i18n.t('documents.delete_template_button'),
              {
                //icon: 'dxre-icon-Delete',
                showText: true,
              },
            ),
            activateTemplateButtonId,
          );
        });
      }
      if (deleteMenuItem && !shouldBeShown) {
        removeDeleteButton();
      }
    },
  );

  useEffect(() => {
    return () => {
      removeDeleteButton();
    };
  }, []);

  useEffect(
    function ShowHideButtonOnDocumentLoad() {
      const richEditCurrent = richEdit?.current;
      richEditCurrent.events.documentLoaded.addHandler(
        addDeleteTemplateMenuItem,
      );
      addDeleteTemplateMenuItem(richEditCurrent, undefined);
      return () => {
        richEditCurrent.events.documentLoaded.removeHandler(
          addDeleteTemplateMenuItem,
        );
      };
    },
    [currentTemplate],
  );

  return null;
};
